import { graphql } from "gatsby";
import React, { useState, useCallback } from "react";
import Markdown from "react-markdown";
import { siteMetadata } from "../../gatsby-config";
import { categoryParams } from "../../node-category";
import { toAbsoluteUrl } from "../utils/absolute-url";
import Breadcrumbs from "../components/breadcrumbs/breadcrumbs";
import CategoryTree from "../components/category-tree/category-tree";
import Layout from "../components/layout";
import ProductList from "../components/product-list/product-list";
import SearchForm from "../components/search-form/search-form";
import Separator from "../components/separator/separator";
import IconsLineCat from "../components/icons-line-cat/icons-line-cat";
import Filter, { filterProducts, ActiveFilters } from "../components/filter/filter"

export const query = graphql`
  query CategoryQuery($slug: String!) {
    strapiCategory(slug: { eq: $slug }) {
      id
      description
      metatitle
      metadescription
      page_header
      page_side
      page_content
      slug
      strapiId
      strapiParent {
          id
        }
      name
      visuel {
        localFile {
          publicURL
          childImageSharp {
            gatsbyImageData(width: 400, height: 400) 
          }
        }
      }
    }
    allStrapiProduct(
      filter: {
        categories: { elemMatch: { slug: { eq: $slug } } }
        status: { eq: "published" }
      }
      sort: {
        position: ASC
      }
    ) {
      edges {
        node {
          slug
          status
          strapiId
          position
          title
          description
          categories {
            name
            id
            slug
          }
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  width: 300
                  height: 365
                  transformOptions: { cropFocus: SOUTH }
                )
              }
            }
          }
          attributs_compositions {
            Nom
            id
          }
          attributs_marquages {
            Nom
            id
          }
          attributs_grammages {
            Nom
            id
          }
          attributs_anses {
            Nom
            id
          }
          attributs_tailles {
            Taille 
            id
          }
          attributs_dimensions {  
            Dimension
            id
          }
          attributs_labels {
            Label
            id
          }
          made_in_france
          best_seller

          variations {
            attributs_couleur {
              Nom
              valeur
              id
            }
            Image {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 300
                    height: 365
                    transformOptions: { cropFocus: SOUTH }
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Category = ({ data, location }) => {
  const [, setFilterVersion] = useState(0);
  const onFilterChange = useCallback(() => setFilterVersion((v) => v + 1), []);

  const category = data.strapiCategory;
  const allProducts = data.allStrapiProduct.edges.map((e) => e.node);
  // define color prop for filter
  for (const product of allProducts) {
    product.attributs_couleur = product.variations.flatMap(el => el.attributs_couleur)

  }

  const currentLocation = typeof window !== "undefined"
    ? { ...location, search: window.location.search }
    : location;
  const products = filterProducts(allProducts, currentLocation);

  const canonicalReplacements = { "sacs-cabas-publicitaires-personnalisees-made-in-france": "cabas-personnalises" };
  const canonicalReplacement = canonicalReplacements[category.slug] || category.slug;

  const seo = {
    metaTitle: category.metatitle || category.name,
    metaDescription: category.metadescription || category.description,
    shareImage: category.visuel,
    article: false,
    canonical: siteMetadata.siteUrl + categoryParams.urlPrefix + canonicalReplacement,
    scripts: [
      {
        type: "application/ld+json",
        content: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "@url": `https://latelierdutotebag.com/${category.slug}`,
          "name": category.name,
          "description": category.description,
          "brand": {
            "@type": "Brand",
            "name": "L'Atelier du Tote Bag"
          },
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "EUR",
            "lowPrice": "0.42",
            "highPrice": "3.96",
            "offerCount": String(allProducts.length),
            "availability": "https://schema.org/InStock"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "650",
            "bestRating": "5",
            "worstRating": "1"
          },
          "mainEntity": [
            {
              "@type": "ItemList",
              "itemListElement": allProducts.map((product, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Product",
                  "name": product.title,
                  "url": `https://latelierdutotebag.com/${product.slug}`,
                  "image": toAbsoluteUrl(product.image.localFile.childImageSharp.gatsbyImageData.images.fallback.src),
                  ...(product.attributs_compositions && product.attributs_compositions.length > 0 && {
                    "material": product.attributs_compositions.map(c => c.Nom).join(", ")
                  }),
                  ...(product.attributs_grammages && product.attributs_grammages.length > 0 && {
                    "weight": {
                      "@type": "QuantitativeValue",
                      "value": product.attributs_grammages[0].Nom.replace(/[^0-9]/g, ''),
                      "unitCode": "GRM",
                      "unitText": "gr/m²"
                    }
                  }),
                  ...(product.made_in_france && {
                    "countryOfOrigin": {
                      "@type": "Country",
                      "name": "France"
                    }
                  }),
                  ...(product.attributs_anses && product.attributs_anses.length > 0 && {
                    "additionalProperty": [{
                      "@type": "PropertyValue",
                      "name": "Taille des anses",
                      "value": product.attributs_anses[0].Nom
                    }]
                  }),
                  "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "reviewCount": "650"
                  }
                }
              }))
            }
          ]
        })
      },
    ]
  };

  return (
    <Layout seo={seo} showHero={false}>
      {category.slug === "DISABLING###cabas-personnalises" && (
        <div >
          <StaticImage src="../assets/icons/bandeau-site-promo-cabas.jpg" className="w-full" alt="Promotion -15% sur tous les cabas personnalisés" />
        </div>
      )}
      <div className="bg-gray-100">
        <div className="container py-4">
          <h1 className="text-center">{category.name}</h1>
          <div className="text-center my-3">
            <Markdown>{category.page_header}</Markdown>
          </div>
        </div>
      </div>
      <div className="py-4">
        <div className="my-4">
          <Breadcrumbs category={category} />
        </div>
        <div className="flex flex-wrap ">
          <div className="w-full order-2 md:order-1 md:w-3/12 md:pr-4">
            {/* <span className="h3 block text-base font-semibold uppercase pb-2 border-b-3 border-gray-200">
              Rechercher un produit
            </span>
            <SearchForm />
            <br /> */}
            <Filter products={allProducts} location={location} onFilterChange={onFilterChange} />



            {/* <div className="cat-side">
              <Markdown>{category.page_side}</Markdown>
            </div> */}
          </div>
          <div className="w-full order-1 md:order-2 md:w-9/12">
            <span className="font-semibold text-gray-700">
              Découvrez tous les produits personnalisés, proposés et fabriqués
              par l’Atelier du Tote Bag.
            </span>
            <ActiveFilters onFilterChange={onFilterChange} />
            <div>Il y a {products.length} produit{products.length > 1 ? 's' : ""}</div>
            <ProductList products={products} />
          </div>
        </div>
        <hr className="my-4" />
        <IconsLineCat />


        <div className="cat-content">
          <Markdown>{category.page_content}</Markdown>
        </div>
        <div className="cat-side">
          <Markdown>{category.page_side}</Markdown>
        </div>
        <div>
          <span className="h3 block mt-8 text-base font-semibold uppercase pb-2 border-b-3 border-gray-200">
            Nos autres sacs personnalisables
          </span>
          <CategoryTree currentCat={category} shouldFindRoot={true} />
        </div>
      </div>
    </Layout>
  );
};

export default Category;
